/**
 * Keeps v-autocomplete selections visible after save (flat FK ids + merge nested relations into :items).
 */
export default {
  data() {
    return {
      selectedDealOption: null,
      stockHeaderReloadSuppressDepth: 0,
      headerOptionsSeed: null,
      headerOptionsSeedSync: null,
    };
  },
  computed: {
    suppressStockHeaderReload() {
      return this.stockHeaderReloadSuppressDepth > 0;
    },
  },
  methods: {
    setHeaderOptionsSeed(data, syncMethod) {
      this.headerOptionsSeed = data;
      this.headerOptionsSeedSync = syncMethod;
    },
    clearHeaderOptionsSeed() {
      this.headerOptionsSeed = null;
      this.headerOptionsSeedSync = null;
    },
    reconcileHeaderOptionsFromSeed() {
      const data = this.headerOptionsSeed;
      const syncMethod = this.headerOptionsSeedSync;
      if (!data || !syncMethod || typeof this[syncMethod] !== "function") {
        return;
      }
      this[syncMethod](data);
    },
    async runStockHeaderReloadOnOrganizationChange() {
      if (this.suppressStockHeaderReload) {
        return;
      }
      await Promise.all([
        typeof this.loadWarehouses === "function" ? this.loadWarehouses() : Promise.resolve(),
        typeof this.loadDeals === "function" ? this.loadDeals() : Promise.resolve(),
      ]);
      this.reconcileHeaderOptionsFromSeed();
    },
    async withSuppressedStockHeaderReload(callback) {
      this.stockHeaderReloadSuppressDepth += 1;
      try {
        await callback();
      } finally {
        await this.$nextTick();
        this.stockHeaderReloadSuppressDepth = Math.max(
          0,
          this.stockHeaderReloadSuppressDepth - 1
        );
        if (this.stockHeaderReloadSuppressDepth === 0) {
          this.reconcileHeaderOptionsFromSeed();
        }
      }
    },
    setSelectedDealCache(deal) {
      if (!deal || !deal.id) {
        return;
      }
      this.selectedDealOption = this.mapDealToOption(deal);
    },
    normalizeDealCompanies(companies) {
      return (companies || [])
        .filter((company) => company && company.id)
        .map((company) => ({
          ...company,
          id: Number(company.id),
          label: company.label || company.name || String(company.id),
        }));
    },
    mapDealToOption(deal) {
      return {
        ...deal,
        id: Number(deal.id),
        label: this.dealOptionLabel(deal),
        suppliers: this.normalizeDealCompanies(deal.suppliers),
        clients: this.normalizeDealCompanies(deal.clients),
      };
    },
    mapDealsToOptions(deals) {
      return (deals || []).map((deal) => this.mapDealToOption(deal));
    },
    resolveDealForParties() {
      const dealId = Number(this.document?.deal_id);
      if (!dealId) {
        return null;
      }
      const fromOptions = (this.dealOptions || []).find(
        (item) => Number(item.id) === dealId
      );
      if (fromOptions) {
        return fromOptions;
      }
      if (
        this.selectedDealOption &&
        Number(this.selectedDealOption.id) === dealId
      ) {
        return this.selectedDealOption;
      }
      if (this.document?.deal && Number(this.document.deal.id) === dealId) {
        return this.document.deal;
      }
      return this.headerOptionsSeed?.deal || null;
    },
    dealPartyFieldMapping() {
      const document = this.document || {};
      if (Object.prototype.hasOwnProperty.call(document, "supplier_company_id")) {
        return { idKey: "supplier_company_id", partiesKey: "suppliers" };
      }
      if (Object.prototype.hasOwnProperty.call(document, "source_company_id")) {
        return { idKey: "source_company_id", partiesKey: "suppliers" };
      }
      if (Object.prototype.hasOwnProperty.call(document, "customer_company_id")) {
        return { idKey: "customer_company_id", partiesKey: "clients" };
      }
      if (Object.prototype.hasOwnProperty.call(document, "destination_company_id")) {
        return { idKey: "destination_company_id", partiesKey: "clients" };
      }
      if (Object.prototype.hasOwnProperty.call(document, "company_id")) {
        return {
          idKey: "company_id",
          partiesKey: document.direction === "incoming" ? "suppliers" : "clients",
        };
      }
      return null;
    },
    preserveDealPartyCompaniesInOptions() {
      const mapping = this.dealPartyFieldMapping();
      const deal = this.resolveDealForParties();
      if (!mapping || !deal || !Array.isArray(this.companyOptions)) {
        return;
      }
      this.normalizeDealCompanies(deal[mapping.partiesKey]).forEach((company) => {
        this.companyOptions = this.mergeOptionIntoList(this.companyOptions, company);
      });
    },
    applyDealPartiesToDocument(deal, { overwriteIfUnique = false } = {}) {
      if (!this.isNew || !deal) {
        return;
      }
      const mapping = this.dealPartyFieldMapping();
      if (!mapping) {
        return;
      }
      const parties = this.normalizeDealCompanies(deal[mapping.partiesKey]);
      parties.forEach((company) => {
        this.companyOptions = this.mergeOptionIntoList(this.companyOptions, company);
      });
      if (!parties.length) {
        return;
      }
      const currentId = this.document[mapping.idKey];
      if (parties.length === 1 && (!currentId || overwriteIfUnique)) {
        this.document[mapping.idKey] = parties[0].id;
      }
    },
    onDealChange(dealId) {
      if (!dealId) {
        this.selectedDealOption = null;
        return;
      }
      const option = (this.dealOptions || []).find(
        (item) => Number(item.id) === Number(dealId)
      );
      if (option) {
        this.selectedDealOption = { ...option };
        this.applyDealPartiesToDocument(option, { overwriteIfUnique: true });
      }
    },
    ensureSelectedDealInOptions(options) {
      const selectedId = this.document.deal_id;
      if (!selectedId) {
        return options;
      }
      const normalized = (options || []).map((item) => ({
        ...item,
        id: Number(item.id),
      }));
      if (normalized.some((item) => Number(item.id) === Number(selectedId))) {
        return normalized;
      }
      if (
        this.selectedDealOption &&
        Number(this.selectedDealOption.id) === Number(selectedId)
      ) {
        return [{ ...this.selectedDealOption, id: Number(this.selectedDealOption.id) }, ...normalized];
      }
      const fromList = (this.dealOptions || []).find(
        (item) => Number(item.id) === Number(selectedId)
      );
      if (fromList) {
        return [{ ...fromList, id: Number(fromList.id) }, ...normalized];
      }
      const seedDeal = this.headerOptionsSeed?.deal;
      if (seedDeal && Number(seedDeal.id) === Number(selectedId)) {
        const option = {
          id: Number(seedDeal.id),
          label: this.dealOptionLabel(seedDeal),
          ...seedDeal,
        };
        return [option, ...normalized];
      }
      return normalized;
    },
    applyDealOptionsResponse(responseData) {
      this.dealOptions = this.ensureSelectedDealInOptions(
        this.mapDealsToOptions(responseData)
      );
      this.reconcileHeaderOptionsFromSeed();
      const deal = this.resolveDealForParties();
      if (deal) {
        this.selectedDealOption = this.mapDealToOption(deal);
      }
      this.applyDealPartiesToDocument(deal);
    },
    seedDealOptionFromId(dealId) {
      const normalizedDealId = Number(dealId);
      if (!normalizedDealId) {
        return;
      }
      const existing = (this.dealOptions || []).find(
        (item) => Number(item.id) === normalizedDealId
      );
      if (existing) {
        this.selectedDealOption = { ...existing, id: Number(existing.id) };
      } else if (
        !this.selectedDealOption ||
        Number(this.selectedDealOption.id) !== normalizedDealId
      ) {
        this.selectedDealOption = {
          id: normalizedDealId,
          label: `#${normalizedDealId}`,
        };
      }
      this.dealOptions = this.ensureSelectedDealInOptions(this.dealOptions);
    },
    mergeOptionIntoList(list, option, buildLabel) {
      if (!option || !option.id) {
        return list;
      }
      const id = Number(option.id);
      if (list.some((item) => Number(item.id) === id)) {
        return list;
      }
      const label =
        typeof buildLabel === "function"
          ? buildLabel(option)
          : option.label || option.name || String(id);
      return [{ id, label, ...option }, ...list];
    },
    syncDealOptionFromDraft(data) {
      const dealId = data.deal_id ?? data.deal?.id;
      if (!dealId) {
        if (!data.deal_id) {
          this.selectedDealOption = null;
        }
        return;
      }
      const normalizedDealId = Number(dealId);
      if (data.deal) {
        this.setSelectedDealCache(data.deal);
      } else if (
        !this.selectedDealOption ||
        Number(this.selectedDealOption.id) !== normalizedDealId
      ) {
        const existing = this.dealOptions.find(
          (item) => Number(item.id) === normalizedDealId
        );
        this.selectedDealOption = existing || {
          id: normalizedDealId,
          label: `#${normalizedDealId}`,
        };
      }
      this.document.deal_id = normalizedDealId;
      this.dealOptions = this.mergeOptionIntoList(
        this.dealOptions,
        this.selectedDealOption,
        (deal) => this.dealOptionLabel(deal)
      );
    },
    syncWarehouseOptionFromDraft(
      data,
      idKey = "destination_warehouse_id",
      objectKey = "destination_warehouse"
    ) {
      const warehouseId = data[idKey] ?? data[objectKey]?.id;
      if (!warehouseId) {
        return;
      }
      if (data[objectKey]) {
        const warehouse = {
          ...data[objectKey],
          label: this.warehouseOptionLabel(data[objectKey]),
        };
        this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, warehouse);
        return;
      }
      const existing = this.warehouseOptions.find(
        (item) => Number(item.id) === Number(warehouseId)
      );
      if (!existing) {
        this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, {
          id: Number(warehouseId),
          label: `#${warehouseId}`,
        });
      }
    },
    warehouseOptionLabel(warehouse) {
      if (!warehouse) {
        return "";
      }
      if (warehouse.label) {
        return warehouse.label;
      }
      let label = "";
      if (warehouse.code && warehouse.name) {
        label = `${warehouse.code} — ${warehouse.name}`;
      } else {
        label = warehouse.name || warehouse.code || String(warehouse.id);
      }
      if (warehouse.type === "virtual" && warehouse.virtual_subtype) {
        const subtypeKey = `STOCK.WAREHOUSES.VIRTUAL_SUBTYPES.${String(
          warehouse.virtual_subtype
        ).toUpperCase()}`;
        const subtype = this.$te(subtypeKey)
          ? this.$t(subtypeKey)
          : warehouse.virtual_subtype;
        return `${label} (${subtype})`;
      }
      return label;
    },
    dealOptionLabel(deal) {
      if (!deal) {
        return "";
      }
      if (deal.label) {
        return deal.label;
      }
      return deal.code ? `${deal.code} — ${deal.name}` : deal.name || String(deal.id);
    },
    mapPurchaseOrderDocument(data) {
      const dealId = data.deal_id ?? data.deal?.id ?? null;
      return {
        organization_id: data.organization_id ?? data.organization?.id ?? null,
        supplier_company_id:
          data.supplier_company_id ?? data.supplier_company?.id ?? null,
        destination_warehouse_id:
          data.destination_warehouse_id ?? data.destination_warehouse?.id ?? null,
        deal_id: dealId != null ? Number(dealId) : null,
        incoterm_id: data.incoterm_id ?? data.incoterm?.id ?? null,
        incoterm_named_place: data.incoterm_named_place ?? null,
      };
    },
    mapReceiptDocument(data) {
      return {
        organization_id: data.organization_id ?? data.organization?.id ?? null,
        source_company_id: data.source_company_id ?? data.source_company?.id ?? null,
        destination_warehouse_id:
          data.destination_warehouse_id ?? data.destination_warehouse?.id ?? null,
        deal_id: data.deal_id ?? data.deal?.id ?? null,
        incoterm_id: data.incoterm_id ?? data.incoterm?.id ?? null,
        incoterm_named_place: data.incoterm_named_place ?? null,
        incoterm_locked: Boolean(data.incoterm_locked),
      };
    },
    syncPurchaseOrderHeaderOptions(data) {
      if (data.organization) {
        this.organizations = this.mergeOptionIntoList(this.organizations, {
          id: data.organization.id,
          label: data.organization.name || data.organization.code || String(data.organization.id),
          ...data.organization,
        });
      }
      if (data.supplier_company) {
        this.companyOptions = this.mergeOptionIntoList(
          this.companyOptions,
          data.supplier_company
        );
      }
      if (data.destination_warehouse) {
        const wh = {
          ...data.destination_warehouse,
          label: this.warehouseOptionLabel(data.destination_warehouse),
        };
        this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, wh);
      } else {
        this.syncWarehouseOptionFromDraft(data);
      }
      this.syncDealOptionFromDraft(data);
    },
    syncReceiptHeaderOptions(data) {
      if (data.organization) {
        this.organizations = this.mergeOptionIntoList(this.organizations, {
          id: data.organization.id,
          label: data.organization.name || data.organization.code || String(data.organization.id),
          ...data.organization,
        });
      }
      if (data.source_company) {
        this.companyOptions = this.mergeOptionIntoList(
          this.companyOptions,
          data.source_company
        );
      }
      if (data.destination_warehouse) {
        const wh = {
          ...data.destination_warehouse,
          label: this.warehouseOptionLabel(data.destination_warehouse),
        };
        this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, wh);
      } else {
        this.syncWarehouseOptionFromDraft(data);
      }
      this.syncDealOptionFromDraft(data);
    },
    async reloadPurchaseOrderHeaderOptions() {
      await Promise.all([this.loadCompanies(), this.loadWarehouses(), this.loadDeals()]);
    },
    async reloadReceiptHeaderOptions() {
      await Promise.all([this.loadCompanies(), this.loadWarehouses(), this.loadDeals()]);
    },
    mapSalesOrderDocument(data) {
      return {
        organization_id: data.organization_id ?? data.organization?.id ?? null,
        customer_company_id: data.customer_company_id ?? data.customer_company?.id ?? null,
        source_warehouse_id: data.source_warehouse_id ?? data.source_warehouse?.id ?? null,
        deal_id: data.deal_id ?? data.deal?.id ?? null,
        incoterm_id: data.incoterm_id ?? data.incoterm?.id ?? null,
        incoterm_named_place: data.incoterm_named_place ?? null,
      };
    },
    mapSaleDocument(data) {
      return {
        organization_id: data.organization_id ?? data.organization?.id ?? null,
        destination_company_id:
          data.destination_company_id ?? data.destination_company?.id ?? null,
        source_warehouse_id: data.source_warehouse_id ?? data.source_warehouse?.id ?? null,
        deal_id: data.deal_id ?? data.deal?.id ?? null,
        incoterm_id: data.incoterm_id ?? data.incoterm?.id ?? null,
        incoterm_named_place: data.incoterm_named_place ?? null,
        incoterm_locked: Boolean(data.incoterm_locked),
      };
    },
    syncSalesOrderHeaderOptions(data) {
      if (data.organization) {
        this.organizations = this.mergeOptionIntoList(this.organizations, {
          id: data.organization.id,
          label: data.organization.name || data.organization.code || String(data.organization.id),
          ...data.organization,
        });
      }
      if (data.customer_company) {
        this.companyOptions = this.mergeOptionIntoList(
          this.companyOptions,
          data.customer_company
        );
      }
      if (data.ship_to_company) {
        this.companyOptions = this.mergeOptionIntoList(
          this.companyOptions,
          data.ship_to_company
        );
      }
      if (data.source_warehouse) {
        const wh = {
          ...data.source_warehouse,
          label: this.warehouseOptionLabel(data.source_warehouse),
        };
        this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, wh);
      } else {
        this.syncWarehouseOptionFromDraft(data, "source_warehouse_id", "source_warehouse");
      }
      this.syncDealOptionFromDraft(data);
    },
    syncSaleHeaderOptions(data) {
      if (data.organization) {
        this.organizations = this.mergeOptionIntoList(this.organizations, {
          id: data.organization.id,
          label: data.organization.name || data.organization.code || String(data.organization.id),
          ...data.organization,
        });
      }
      if (data.destination_company) {
        this.companyOptions = this.mergeOptionIntoList(
          this.companyOptions,
          data.destination_company
        );
      }
      if (data.source_warehouse) {
        const wh = {
          ...data.source_warehouse,
          label: this.warehouseOptionLabel(data.source_warehouse),
        };
        this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, wh);
      } else {
        this.syncWarehouseOptionFromDraft(data, "source_warehouse_id", "source_warehouse");
      }
      this.syncDealOptionFromDraft(data);
    },
    async reloadSalesOrderHeaderOptions() {
      await Promise.all([this.loadCompanies(), this.loadWarehouses(), this.loadDeals()]);
    },
    async reloadSaleHeaderOptions() {
      await Promise.all([this.loadCompanies(), this.loadWarehouses(), this.loadDeals()]);
    },
    syncTransportOrderHeaderOptions(data) {
      if (data.organization) {
        this.organizations = this.mergeOptionIntoList(this.organizations, {
          id: data.organization.id,
          label: data.organization.name || data.organization.code || String(data.organization.id),
          ...data.organization,
        });
      }
      if (data.carrier_company) {
        this.companyOptions = this.mergeOptionIntoList(this.companyOptions, {
          id: data.carrier_company.id,
          label: data.carrier_company.label || data.carrier_company.name,
          ...data.carrier_company,
        });
      }
      (data.stops || []).forEach((stop) => {
        if (stop.company) {
          this.companyOptions = this.mergeOptionIntoList(this.companyOptions, {
            id: stop.company.id,
            label: stop.company.label || stop.company.name,
            ...stop.company,
          });
        }
      });
      this.syncDealOptionFromDraft(data);
    },
    async reloadTransportOrderHeaderOptions() {
      await Promise.all([this.loadCompanies(), this.loadDeals()]);
    },
  },
};
